import { oc } from "@orpc/contract";
import { z } from "zod";
import {
  ArInvoiceListItemSchema,
  agingBasisSchema,
} from "./ar-invoices.contract";
import { EmailImportanceSchema } from "../email-drafts.contract";
import { sendFromOverrideSchema } from "./ar-email-shared.contract";

export const invoiceHighlightColorSchema = z.enum(["yellow", "red", "green"]);

export type InvoiceHighlightColor = z.infer<typeof invoiceHighlightColorSchema>;

export const invoiceHighlightSchema = z.object({
  invoiceId: z.string().uuid(),
  color: invoiceHighlightColorSchema,
});

export type InvoiceHighlight = z.infer<typeof invoiceHighlightSchema>;

export const arStatementEmailInputSchema = z.object({
  customerId: z.string().uuid(),
  recipientEmails: z.array(z.string().email()).min(1),
  /**
   * Explicit Cc list. When present (even empty), every `recipientEmails` entry
   * is addressed as To and this list as Cc — required for the org's
   * `statementRecipientRoles` setting to survive the send. When absent, the
   * server falls back to the legacy positional split of `recipientEmails`
   * (first address To, the rest Cc).
   */
  ccRecipientEmails: z.array(z.string().email()).optional(),
  minDays: z.number().int().min(0).optional(),
  maxDays: z.number().int().min(0).optional(),
  /** When true, restrict to invoices not yet due (reference date >= today).
   *  Only meaningful with basis="days_overdue". Overrides minDays/maxDays. */
  currentOnly: z.boolean().optional(),
  basis: agingBasisSchema.optional(),
  excludeInvoiceIds: z.array(z.string().uuid()).optional(),
  subjectOverride: z.string().min(1).optional(),
  htmlContentOverride: z.string().min(1).optional(),
  /** Per-send override for the PDF attachment filename. Sanitized server-side. */
  pdfFilenameOverride: z.string().min(1).max(255).optional(),
  invoiceHighlights: z.array(invoiceHighlightSchema).optional(),
  /**
   * Telemetry only: did the user actually edit the email subject or body before
   * sending? The client computes this by diffing the draft against the
   * platform-generated baseline. Because the send payload always carries the
   * subject/body as overrides (the draft is seeded from the preview), the server
   * cannot infer this from override presence — so the client reports it
   * explicitly. When omitted, the server falls back to override-presence.
   */
  editedBody: z.boolean().optional(),
  /**
   * Per-send routing for the sending mailbox. Omit to use the AR module
   * config (`configured` mode), which falls back to SendGrid when no AR
   * outbox is configured.
   */
  sendFrom: sendFromOverrideSchema.optional(),
  /** When true, BCC the sending user so they receive a copy of the statement. */
  bccSender: z.boolean().optional(),
  /** Additional BCC addresses (contacts or ad-hoc) beyond the sender. */
  bccRecipientEmails: z.array(z.string().email()).optional(),
  /** Additional manually-entered email address (persisted as a default for future sends). */
  customEmail: z.string().email().optional(),
  /** Mark the outgoing statement email's priority. Defaults to "normal". */
  importance: EmailImportanceSchema.optional(),
});

export const arStatementEmailPreviewOutputSchema = z.object({
  from: z.string(),
  to: z.string(),
  cc: z.array(z.string()),
  bcc: z.array(z.string()),
  subject: z.string(),
  htmlContent: z.string(),
  pdfBase64: z.string(),
  pdfFilename: z.string(),
  invoices: z.array(ArInvoiceListItemSchema),
  templateVariables: z.record(z.string(), z.string()),
});

/**
 * One row of the post-send statement summary: who the statement went to, and
 * the oldest invoice it actually contained.
 *
 * Populated by the send handler from the invoice set the server resolved, so a
 * bulk run can produce a report covering customers the user never opened. The
 * *derivation* is ERP-specific and today Famous-only — see
 * `famousStatementSendSummary.ts` on the backend for the swappable seam.
 *
 * Field schemas are borrowed from `ArInvoiceListItemSchema` so the summary can
 * never disagree with the invoice rows it was derived from.
 */
export const arStatementSendSummarySchema = z.object({
  /** Customer name as the source ERP renders it (Famous: `Acme Foods (ACM)`). */
  customerErpDisplayName: z.string(),
  oldestInvoiceNumber: ArInvoiceListItemSchema.shape.invoiceNumber,
  oldestInvoiceDate: ArInvoiceListItemSchema.shape.invoiceDate,
  oldestInvoiceBalanceDue: ArInvoiceListItemSchema.shape.balanceDue,
});

export type ArStatementSendSummary = z.infer<
  typeof arStatementSendSummarySchema
>;

export const arStatementEmailContracts = {
  preview: oc
    .input(arStatementEmailInputSchema)
    .output(arStatementEmailPreviewOutputSchema)
    .route({
      method: "POST",
      summary: "Preview a customer statement email",
      description:
        "Renders a customer's account statement email and PDF without sending, filtered by aging range or current-only invoices, with exclusions, highlights, and overrides applied.",
    }),

  send: oc
    .input(arStatementEmailInputSchema)
    .output(
      z.object({
        outboundEmailId: z.string().uuid(),
        summary: arStatementSendSummarySchema,
      }),
    )
    .route({
      method: "POST",
      summary: "Email a statement to a customer",
      description:
        "Sends a customer's account statement PDF covering the selected open invoices to the given recipients. Returns a summary naming the oldest invoice included.",
    }),
} as const;
